var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/**
 * [CLAIMS_CONTEXT]
 * purpose: Allows the user to review and confirm their claim information (claim date, contact details, email, phone number and address) before proceeding with the qualification step
 * touches: declaration, qualification, claim, contract
 * invariants: The verification choices are synchronized with the claim extraData; displayed information comes from the selected contract contact and claim data
 * inputs: claim information, selectedContract contact, claim extraData, YES_OR_NO_CHOICES
 * outputs: Updates claim extraData through updateAndSaveClaim after all information has been checked; displays the validation state of each user information field
 * see: @declaration/store/claim, @declaration/store/contract, @shared/types/api/ExtraData/DynarenExtraData, @shared/types/api/contact
 */
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import { UiButton, UiRadio } from '@claims-ia/design-system';
import QuestionPanel from '@declaration/components/UI/QuestionPanel.vue';
import { QuestionComponent, QuestionProps } from '@declaration/question';
import { YES_OR_NO_CHOICES } from '@declaration/helpers/choices';
const claim = namespace('claim');
const contract = namespace('contract');
let UserInformationCheckingQuestion = class UserInformationCheckingQuestion extends QuestionComponent {
    constructor() {
        super(...arguments);
        this.userInfo = {
            isClaimDateOk: null,
            isContactInfoOk: null,
            isEmailOk: null,
            isPhoneOk: null,
            isAddressOk: null,
        };
    }
    get yesNoChoices() {
        return YES_OR_NO_CHOICES.map((choice) => ({
            value: choice.value,
            label: this.$tc(choice.tradKey),
        }));
    }
    get extraData() {
        var _a;
        return ((_a = this.claimInfo) === null || _a === void 0 ? void 0 : _a.extraData) || {};
    }
    mounted() {
        const { isClaimDateOk = null, isContactInfoOk = null, isEmailOk = null, isPhoneOk = null, isAddressOk = null, } = this.extraData;
        this.userInfo = {
            isClaimDateOk,
            isContactInfoOk,
            isEmailOk,
            isPhoneOk,
            isAddressOk,
        };
    }
    get userExtraDataStore() {
        var _a, _b, _c, _d, _e;
        return {
            isClaimDateOk: (_a = this.extraData.isClaimDateOk) !== null && _a !== void 0 ? _a : null,
            isContactInfoOk: (_b = this.extraData.isContactInfoOk) !== null && _b !== void 0 ? _b : null,
            isEmailOk: (_c = this.extraData.isEmailOk) !== null && _c !== void 0 ? _c : null,
            isPhoneOk: (_d = this.extraData.isPhoneOk) !== null && _d !== void 0 ? _d : null,
            isAddressOk: (_e = this.extraData.isAddressOk) !== null && _e !== void 0 ? _e : null,
        };
    }
    get contact() {
        var _a;
        return (((_a = this.selectedContract) === null || _a === void 0 ? void 0 : _a.contact) || {});
    }
    get fullName() {
        const name = `${this.contact.firstName || ''} ${this.contact.lastName || ''}`.trim();
        return name || this.$tc('generic.unknown');
    }
    get formattedClaimDate() {
        var _a;
        const damageDate = (_a = this.claimInfo) === null || _a === void 0 ? void 0 : _a.damageDate;
        if (!damageDate) {
            return this.$tc('generic.unknown');
        }
        return new Date(damageDate).toLocaleDateString('fr-FR');
    }
    get claimAddress() {
        return ([this.contact.address, this.contact.addressAffix, this.contact.zipcode, this.contact.city]
            .filter(Boolean)
            .join(' ') || this.$tc('generic.unknown'));
    }
    get contactEmail() {
        return this.contact.email || this.$tc('generic.unknown');
    }
    get contactMobilePhone() {
        return this.contact.mobilePhone || this.$tc('generic.unknown');
    }
    get allInformationChecked() {
        return [
            this.userInfo.isClaimDateOk,
            this.userInfo.isContactInfoOk,
            this.userInfo.isEmailOk,
            this.userInfo.isPhoneOk,
            this.userInfo.isAddressOk,
        ].every((value) => value !== null);
    }
    get answer() {
        return [
            this.userExtraDataStore.isClaimDateOk,
            this.userExtraDataStore.isContactInfoOk,
            this.userExtraDataStore.isEmailOk,
            this.userExtraDataStore.isPhoneOk,
            this.userExtraDataStore.isAddressOk,
        ].every((value) => value !== null)
            ? this.$tc('claim.qualification.user_information_checking.information_verified')
            : null;
    }
    get panelTitle() {
        return this.$tc('claim.qualification.user_information_checking.title');
    }
    get sidebarTitle() {
        const key = 'claim.qualification.user_information_checking.sidebar_title';
        return this.$te(key) ? this.$tc(key) : this.panelTitle;
    }
    get description() {
        return this.$tc('claim.qualification.user_information_checking.description');
    }
    get yesLabel() {
        var _a;
        return this.$tc(((_a = YES_OR_NO_CHOICES.find((choice) => choice.value === true)) === null || _a === void 0 ? void 0 : _a.tradKey) || '');
    }
    get noLabel() {
        var _a;
        return this.$tc(((_a = YES_OR_NO_CHOICES.find((choice) => choice.value === false)) === null || _a === void 0 ? void 0 : _a.tradKey) || '');
    }
    confirmInformation() {
        this.updateAndSaveClaim({
            claimPayload: {
                extraData: {
                    ...this.extraData,
                    ...this.userInfo,
                },
            },
            step: this.step,
        });
    }
};
__decorate([
    claim.State('isUpdatingClaim')
], UserInformationCheckingQuestion.prototype, "isUpdatingClaim", void 0);
__decorate([
    claim.State('claim')
], UserInformationCheckingQuestion.prototype, "claimInfo", void 0);
__decorate([
    claim.Action('updateAndSaveClaim')
], UserInformationCheckingQuestion.prototype, "updateAndSaveClaim", void 0);
__decorate([
    contract.State('selectedContract')
], UserInformationCheckingQuestion.prototype, "selectedContract", void 0);
UserInformationCheckingQuestion = __decorate([
    Component({
        components: {
            QuestionPanel,
            UiButton,
            UiRadio,
        },
        props: QuestionProps,
    })
], UserInformationCheckingQuestion);
export default UserInformationCheckingQuestion;
